/* Preview reads native variants; Shopify remains responsible for price and purchase. */
if (!customElements.get('slich-lenticular-preview')) {
  customElements.define('slich-lenticular-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.host = this.closest('product-info');
      this.gallery = this.parentElement.querySelector('media-gallery');
      this.frame = this.querySelector('[data-viewer-frame]');
      this.panel = this.querySelector('.slich-lenticular-panel');
      this.error = this.querySelector('.slich-lenticular-error');
      this.wait = this.querySelector('.slich-lenticular-wait');
      this.comparison = this.parentElement.querySelector('[data-lenticular-comparison]');
      if (!this.host || !this.gallery || !this.frame) return;
      // Keep the full native gallery first when JavaScript or the iframe fails.
      if (this.comparison) this.gallery.after(this.comparison);
      try { this.data = JSON.parse(this.querySelector('[data-lenticular-variants]').textContent); } catch { return; }
      if (!Array.isArray(this.data.variants) || !this.data.variants.length) return;
      const ids = new Set(['sillage', 'ecailles', 'maree', 'dahlia', 'hyperespace', 'ballet', 'declic', 'orbites', 'cadence', 'vertige']);
      this.previewable = variant => ids.has(variant?.designId) && typeof variant.framed === 'boolean';
      this.mode = 'interactive';
      this.inView = true;
      this.ready = false;
      this.origin = 'https://geoprint-shopify.vercel.app';
      this.buttons = [...this.querySelectorAll('button[data-mode]')];
      this.buttons.find(b => b.dataset.mode === 'photos')?.setAttribute('aria-controls', this.gallery.id);
      this.onClick = event => {
        const button = event.target.closest('button[data-mode]');
        if (button && this.contains(button)) this.setMode(button.dataset.mode);
      };
      this.onChange = event => {
        if (event.target.closest('variant-selects') && this.host.contains(event.target)) {
          this.userSelected = true;
          this.sync();
        }
      };
      this.onMessage = event => {
        if (event.origin !== this.origin || event.source !== this.frame.contentWindow || event.data?.type !== 'slich:lenticulaire:ready') return;
        clearTimeout(this.timeout);
        this.ready = true;
        this.wait.hidden = true;
        this.error.hidden = true;
        this.sync();
      };
      this.onPageShow = () => this.sync();
      this.addEventListener('click', this.onClick);
      document.addEventListener('change', this.onChange);
      window.addEventListener('message', this.onMessage);
      window.addEventListener('pageshow', this.onPageShow);
      this.frame.addEventListener('load', this.onPageShow);
      if (typeof IntersectionObserver === 'function') {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0]?.isIntersecting ?? true;
          this.sync();
        }, { rootMargin: '80px' });
        this.observer.observe(this);
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event?.data?.sectionId) === this.dataset.section) {
            this.lastMediaVariant = null;
            this.sync();
          }
        });
      }
      this.querySelector('.slich-lenticular-tabs').hidden = false;
      const selected = this.selection();
      if (this.previewable(selected)) {
        this.error.hidden = true;
        this.setMode('interactive');
      } else {
        this.fallback();
      }
      // Do not override an explicit URL, a user choice, or a restored history entry.
      const explicit = new URL(location.href).searchParams.has('variant');
      const restored = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
      if (!explicit && !restored) Promise.all(['variant-selects', 'product-info'].map(name => customElements.whenDefined(name))).then(() => {
        if (!this.isConnected || this.userSelected) return;
        const current = this.selection();
        const candidates = this.data.variants.filter(v => v.available && v.designId === current?.designId && Number.isFinite(v.price));
        const cheapest = candidates.sort((a, b) => a.price - b.price)[0];
        if (cheapest && cheapest.id !== current?.id) this.selectNativeVariant(cheapest);
      });
      this.sync();
    }
    selectNativeVariant(variant) {
      const groups = this.optionGroups();
      if (groups.length !== variant.options.length) return;
      const controls = groups.map((group, index) => group.matches('select') ? [...group.options].find(option => option.value === variant.options[index]) : [...group.querySelectorAll('input')].find(input => input.value === variant.options[index]));
      if (controls.some(control => !control)) return;
      let changed;
      controls.forEach((control, index) => {
        if (groups[index].matches('select')) {
          if (groups[index].value !== control.value) changed = groups[index];
          groups[index].value = control.value;
          [...groups[index].options].forEach(option => option.toggleAttribute('selected', option === control));
        } else {
          if (!control.checked) changed = control;
          control.checked = true;
        }
      });
      changed?.dispatchEvent(new Event('change', { bubbles: true }));
    }
    optionGroups() {
      const picker = [...this.host.querySelectorAll('variant-selects')].find(element => element.style.display !== 'none');
      return picker ? [...picker.querySelectorAll('fieldset, select')] : [];
    }
    selection() {
      const values = this.optionGroups().map(group => group.matches('select') ? group.value : group.querySelector('input:checked')?.value);
      const byOptions = values.length && this.data.variants.find(v => v.options.length === values.length && v.options.every((value, index) => value === values[index]));
      const selectedId = this.host.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
      return byOptions || this.data.variants.find(v => String(v.id) === selectedId) || this.data.variants.find(v => String(v.id) === String(this.data.selected));
    }
    sync() {
      const variant = this.selection();
      if (!variant) return;
      this.querySelector('[data-selection-label]').textContent = variant.options.join(' · ');
      const introPrice = this.host.querySelector('[data-lenticular-current-price]');
      if (introPrice) introPrice.textContent = Number.isFinite(variant.price) ? new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency: this.data.currency || 'EUR' }).format(variant.price / 100) : '';
      const introChoice = this.host.querySelector('[data-lenticular-current-option]');
      if (introChoice) introChoice.textContent = variant.options.join(' · ');
      this.comparison?.querySelectorAll('.slich-lenticular-choice').forEach(link => {
        const target = this.data.variants.find(v => v.designId === link.dataset.designId && v.options[1] === variant.options[1]);
        link.removeAttribute('aria-current');
        if (!target) {
          link.hidden = true;
          return;
        }
        link.hidden = false;
        const url = new URL(link.href, location.href);
        url.searchParams.set('variant', String(target.id));
        link.href = url.href;
        if (String(target.id) === String(variant.id)) link.setAttribute('aria-current', 'true');
        const price = link.querySelector('[data-choice-price]');
        if (price && Number.isFinite(target.price)) price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency: this.data.currency || 'EUR' }).format(target.price / 100);
        const status = link.querySelector('[data-choice-status]');
        if (status) status.hidden = target.available;
      });
      if (this.mode === 'photos') this.showSelectedVideo(variant);
      if (!this.previewable(variant)) {
        this.fallback();
        return;
      }
      this.frame.contentWindow?.postMessage({ type: 'slich:lenticulaire:selection', designId: variant.designId, framed: variant.framed, formatLabel: variant.options[1], active: this.mode === 'interactive' && this.inView }, this.origin);
    }
    showSelectedVideo(variant) {
      if (this.lastMediaVariant === variant?.id) return;
      this.lastMediaVariant = variant?.id;
      const motif = variant?.options[0];
      if (!motif) return;
      const media = [...this.gallery.querySelectorAll('li[data-media-id]')].find(item => {
        const video = item.querySelector('video') || item.querySelector('template')?.content.querySelector('video');
        return video?.getAttribute('aria-label')?.startsWith(`${motif} —`);
      });
      if (media) this.gallery.setActiveMedia?.(media.dataset.mediaId, false);
    }
    loadViewer(variant) {
      if (!this.previewable(variant)) return;
      clearTimeout(this.timeout);
      this.frame.src = `${this.origin}/shopify/tableau-lenticulaire?motif=${encodeURIComponent(variant.designId)}&cadre=${variant.framed ? '1' : '0'}&format=${variant.options[1]?.startsWith('20') ? '20x30' : '30x40'}`;
      this.failed = false;
      this.ready = false;
      this.wait.hidden = false;
      this.error.hidden = true;
      this.timeout = setTimeout(() => this.fallback(), 15000);
    }
    fallback() {
      clearTimeout(this.timeout);
      this.failed = true;
      this.setMode('photos');
      this.error.hidden = false;
      this.wait.hidden = true;
    }
    setMode(mode) {
      if (!['interactive', 'photos'].includes(mode)) return;
      const variant = this.selection();
      if (mode === 'interactive' && !this.previewable(variant)) mode = 'photos';
      this.mode = mode;
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mode === mode)));
      this.panel.hidden = mode !== 'interactive';
      this.parentElement.classList.toggle('slich-lenticular-interactive', mode === 'interactive');
      this.gallery.inert = mode === 'interactive';
      if (mode === 'interactive') this.gallery.setAttribute('aria-hidden', 'true');
      else this.gallery.removeAttribute('aria-hidden');
      if (mode === 'interactive') this.parentElement.querySelectorAll('media-gallery video').forEach(video => video.pause());
      // Do not call sync here: an incomplete selected variant must not recurse.
      if (mode === 'interactive' && (!this.frame.getAttribute('src') || this.failed)) this.loadViewer(variant);
      if (this.previewable(variant)) this.frame.contentWindow?.postMessage({ type: 'slich:lenticulaire:selection', designId: variant.designId, framed: variant.framed, formatLabel: variant.options[1], active: mode === 'interactive' && this.inView }, this.origin);
      if (mode === 'photos') {
        this.lastMediaVariant = null;
        this.showSelectedVideo(variant);
        window.dispatchEvent(new Event('resize'));
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timeout);
      this.observer?.disconnect();
      this.unsubscribe?.();
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('change', this.onChange);
      window.removeEventListener('message', this.onMessage);
      window.removeEventListener('pageshow', this.onPageShow);
      this.frame?.removeEventListener('load', this.onPageShow);
      this.parentElement?.classList.remove('slich-lenticular-interactive');
      if (this.gallery) {
        this.gallery.inert = false;
        this.gallery.removeAttribute('aria-hidden');
      }
      this.initialized = false;
    }
  });
}
